/**
 * CustomerChatTab - Aba de conversas do contato principal do cliente
 * Layout premium estilo WhatsApp Web com:
 * - Ícones coloridos por canal
 * - Bubbles de preview das mensagens
 * - Status animado para conversas ativas
 * - Grid responsivo para telas grandes
 * 
 * Versão: 2.0.0
 * RELEASE: 2026-01-10
 * - Redesign completo com visual premium (mesmo do DealChatTab)
 * - Suporte a lazy loading via prop isActive
 */
import React, { useState } from 'react';
import { StartChatInboxPicker } from '@/components/crm/deals/StartChatInboxPicker';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { 
  User,
  ExternalLink,
  MessageCircle,
  MessageSquare,
  Instagram,
  MessagesSquare,
  Send,
  Globe,
  Mail,
  Clock,
  CheckCircle2,
  Sparkles,
  Phone
} from 'lucide-react';
import { formatInboxIdentifierForDisplay } from '@/lib/phone-utils';
import { useContactConversations } from '@/hooks/useContactConversations';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';

// Props do componente
interface CustomerChatTabProps {
  contactId: string | null;
  /** Nome do contato (apenas UX no picker de inbox) */
  contactName?: string | null;
  onOpenConversation?: (conversationId: string) => void;
  isActive?: boolean; // Para lazy loading
}

// Configuração visual por canal - cores e ícones específicos
const channelConfig: Record<string, { 
  icon: React.ElementType; 
  color: string; 
  bg: string; 
  label: string;
  ringColor: string;
}> = {
  whatsapp: { 
    icon: MessageSquare, 
    color: 'text-green-600 dark:text-green-400', 
    bg: 'bg-green-500/10 dark:bg-green-500/20', 
    label: 'WhatsApp',
    ringColor: 'ring-green-500/30'
  },
  whatsapp_oficial: { 
    icon: MessageSquare, 
    color: 'text-emerald-600 dark:text-emerald-400', 
    bg: 'bg-emerald-500/10 dark:bg-emerald-500/20', 
    label: 'WhatsApp Business',
    ringColor: 'ring-emerald-500/30'
  },
  instagram: { 
    icon: Instagram, 
    color: 'text-pink-500 dark:text-pink-400', 
    bg: 'bg-gradient-to-br from-pink-500/10 to-purple-500/10 dark:from-pink-500/20 dark:to-purple-500/20', 
    label: 'Instagram',
    ringColor: 'ring-pink-500/30'
  },
  messenger: { 
    icon: MessagesSquare, 
    color: 'text-blue-500 dark:text-blue-400', 
    bg: 'bg-blue-500/10 dark:bg-blue-500/20', 
    label: 'Messenger',
    ringColor: 'ring-blue-500/30'
  },
  telegram: { 
    icon: Send, 
    color: 'text-sky-500 dark:text-sky-400', 
    bg: 'bg-sky-500/10 dark:bg-sky-500/20', 
    label: 'Telegram',
    ringColor: 'ring-sky-500/30'
  },
  webchat: { 
    icon: Globe, 
    color: 'text-purple-500 dark:text-purple-400', 
    bg: 'bg-purple-500/10 dark:bg-purple-500/20', 
    label: 'WebChat',
    ringColor: 'ring-purple-500/30'
  },
  email: { 
    icon: Mail, 
    color: 'text-orange-500 dark:text-orange-400', 
    bg: 'bg-orange-500/10 dark:bg-orange-500/20', 
    label: 'Email',
    ringColor: 'ring-orange-500/30'
  },
};

// Configuração visual por status da conversa
const statusConfig: Record<string, { 
  label: string; 
  className: string;
  icon?: React.ElementType;
}> = {
  open: {
    label: 'Ativa',
    className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 border-green-200 dark:border-green-800'
  },
  pending: {
    label: 'Pendente',
    className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 border-amber-200 dark:border-amber-800',
    icon: Clock
  },
  resolved: {
    label: 'Resolvida',
    className: 'bg-slate-100 text-slate-600 dark:bg-slate-800/50 dark:text-slate-400 border-slate-200 dark:border-slate-700',
    icon: CheckCircle2
  },
  closed: {
    label: 'Fechada',
    className: 'bg-slate-100 text-slate-600 dark:bg-slate-800/50 dark:text-slate-400 border-slate-200 dark:border-slate-700',
    icon: CheckCircle2
  }
};

// Configuração padrão para canais não mapeados
const defaultChannelConfig = {
  icon: MessageCircle,
  color: 'text-muted-foreground',
  bg: 'bg-muted',
  label: 'Chat',
  ringColor: 'ring-muted'
};

/**
 * Aba de chat/conversas do contato principal do cliente - Visual Premium
 */
export function CustomerChatTab({ contactId, contactName, onOpenConversation, isActive }: CustomerChatTabProps) {
  // Controla o picker de inbox para iniciar nova conversa quando não há nenhuma
  const [startChatOpen, setStartChatOpen] = useState(false);

  // Lazy loading: só busca dados quando aba está ativa
  const { data: conversations, isLoading, error } = useContactConversations(
    contactId, 
    { enabled: isActive ?? true }
  );

  // Sem contato vinculado
  if (!contactId) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="relative">
          <div className="h-16 w-16 rounded-full bg-gradient-to-br from-muted to-muted/50 flex items-center justify-center">
            <User className="h-8 w-8 text-muted-foreground/50" />
          </div>
        </div>
        <h3 className="text-sm font-medium mt-4">Nenhum contato vinculado</h3>
        <p className="text-xs text-muted-foreground max-w-[280px] mt-1">
          Este cliente não possui um contato principal para exibir conversas.
        </p>
      </div>
    );
  }

  // Loading state com skeleton premium
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="p-4 rounded-xl border bg-card">
            <div className="flex items-start gap-3">
              <Skeleton className="h-12 w-12 rounded-xl shrink-0" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-12 w-full rounded-xl" />
                <div className="flex gap-2">
                  <Skeleton className="h-5 w-16 rounded-full" />
                  <Skeleton className="h-5 w-12 rounded-full" />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="h-16 w-16 rounded-full bg-destructive/10 flex items-center justify-center">
          <MessageCircle className="h-8 w-8 text-destructive/50" />
        </div>
        <p className="text-sm text-muted-foreground mt-4">Erro ao carregar conversas.</p>
      </div>
    );
  }

  // Empty state premium — agora com CTA para iniciar conversa via picker de inbox
  if (!conversations || conversations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="relative">
          <div className="h-20 w-20 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
            <MessageCircle className="h-10 w-10 text-primary/40" />
          </div>
          <div className="absolute -bottom-1 -right-1 h-8 w-8 rounded-full bg-background flex items-center justify-center border-2 border-background shadow-sm">
            <Sparkles className="h-4 w-4 text-muted-foreground" />
          </div>
        </div>
        <h3 className="text-sm font-medium mt-4">Nenhuma conversa</h3>
        <p className="text-xs text-muted-foreground max-w-[280px] mt-1">
          Este cliente ainda não iniciou nenhuma conversa por nenhum canal.
        </p>

        {contactId && (
          <>
            <Button
              size="sm"
              className="mt-4 gap-2"
              onClick={() => setStartChatOpen(true)}
            >
              <MessageCircle className="h-4 w-4" />
              Iniciar conversa
            </Button>
            <StartChatInboxPicker
              open={startChatOpen}
              onOpenChange={setStartChatOpen}
              contactId={contactId}
              contactName={contactName}
              onConversationReady={(conversationId) => {
                onOpenConversation?.(conversationId);
              }}
            />
          </>
        )}
      </div>
    );
  }

  return (
    /**
     * v2.1.0: Container com scroll vertical próprio
     * Agora que o ScrollArea global foi removido do modal,
     * cada aba controla seu próprio overflow
     */
    <div className="h-full overflow-y-auto">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {conversations.map((conversation) => {
          // Configuração do canal
          const channelType = conversation.inbox?.channel_type || 'webchat';
          const channel = channelConfig[channelType] || defaultChannelConfig;
          const ChannelIcon = channel.icon;

          // Configuração do status
          const status = statusConfig[conversation.status] || statusConfig.open;
          const isActiveConversation = conversation.status === 'open';
          const StatusIcon = status.icon;

          return (
            <div
              key={conversation.id}
              className={cn(
                "relative p-4 rounded-xl border bg-card transition-all duration-200 cursor-pointer group",
                "hover:shadow-md hover:border-primary/20 hover:scale-[1.01]",
                isActiveConversation && "ring-1 ring-green-500/20"
              )}
              onClick={() => onOpenConversation?.(conversation.id)}
            >
              <div className="flex items-start gap-3">
                {/* Ícone do Canal com Status Animado */}
                <div className="relative shrink-0">
                  <div className={cn(
                    "h-12 w-12 rounded-xl flex items-center justify-center ring-1",
                    channel.bg,
                    channel.ringColor
                  )}>
                    <ChannelIcon className={cn("h-6 w-6", channel.color)} />
                  </div>
                  
                  {/* Indicador de status ativo (pulsação) */}
                  {isActiveConversation && (
                    <span className="absolute -bottom-0.5 -right-0.5 flex h-3.5 w-3.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                      <span className="relative inline-flex rounded-full h-3.5 w-3.5 bg-green-500 ring-2 ring-background" />
                    </span>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                {/* Header: Nome da Inbox + Timestamp */}
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="text-sm font-medium truncate">
                      {conversation.inbox?.name || channel.label}
                    </span>
                    {conversation.last_message_at && (
                      <span className="text-[10px] text-muted-foreground flex items-center gap-1 shrink-0">
                        <Clock className="h-3 w-3" />
                        {formatDistanceToNow(new Date(conversation.last_message_at), { 
                          addSuffix: false, 
                          locale: ptBR 
                        })}
                      </span>
                    )}
                  </div>

                  {/* Número do WhatsApp/Telefone da Inbox */}
                  {conversation.inbox?.identifier && (
                    <div className="flex items-center gap-1 mb-2">
                      <Phone className="h-3 w-3 text-muted-foreground" />
                      <span className="text-[11px] text-muted-foreground">
                        {formatInboxIdentifierForDisplay(conversation.inbox.channel_type, conversation.inbox.identifier)}
                      </span>
                    </div>
                  )}

                  {/* Bubble de Preview estilo Chat */}
                  <div className="bg-muted/50 dark:bg-muted/30 rounded-2xl rounded-tl-sm px-3 py-2 mb-2.5">
                    <p className="text-xs text-muted-foreground line-clamp-2">
                      {conversation.last_message_preview || 'Sem mensagens'}
                    </p>
                  </div>

                  {/* Footer: Status + Unread Count */}
                  <div className="flex items-center gap-2">
                    <span 
                      className={cn(
                        "inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-medium border",
                        status.className
                      )}
                    >
                      {StatusIcon && <StatusIcon className="h-3 w-3" />}
                      {status.label}
                    </span>
                    
                    {conversation.unread_count > 0 && (
                      <Badge 
                        variant="destructive" 
                        className="h-5 px-1.5 text-[10px] font-semibold"
                      >
                        {conversation.unread_count} não {conversation.unread_count === 1 ? 'lida' : 'lidas'}
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Botão de abrir conversa */}
                <Button
                  variant="ghost"
                  size="icon"
                  className={cn(
                    "h-8 w-8 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity",
                    "hover:bg-primary/10 hover:text-primary"
                  )}
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenConversation?.(conversation.id);
                  }}
                >
                  <ExternalLink className="h-4 w-4" />
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
